import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

export default function HelpPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const categories = [
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
        </svg>
      ),
      title: 'Buying Tickets',
      description: 'How to purchase, payment options, and delivery'
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: 'Selling Tickets',
      description: 'Listing, pricing, and getting paid'
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      title: 'Buyer Guarantee',
      description: 'Your purchase protection explained'
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
      ),
      title: 'Refunds & Returns',
      description: 'Cancellation and refund policies'
    }
  ];

  const faqs = [
    {
      question: 'How do I know my tickets are authentic?',
      answer: 'Every ticket sold on BESTIX is backed by our 100% Buyer Guarantee. We verify all sellers and tickets before they reach you. If there\'s ever an issue with ticket authenticity, you\'ll receive a full refund or replacement tickets.'
    },
    {
      question: 'When will I receive my tickets?',
      answer: 'Delivery times vary by event and ticket type. Most electronic tickets are delivered within minutes of purchase. Physical tickets and transfers are typically delivered 1-3 days before the event. You can track your order status in your account dashboard.'
    },
    {
      question: 'Can I return or exchange my tickets?',
      answer: 'All sales are final once confirmed. However, our Buyer Guarantee covers you if: the event is cancelled, tickets are invalid, or tickets don\'t arrive in time. In these cases, you\'ll receive a full refund. See our Refund Policy for details.'
    },
    {
      question: 'Why are ticket prices higher than face value?',
      answer: 'BESTIX is a marketplace where sellers set their own prices based on demand. Prices reflect market value and may be above or below face value. All final prices, including fees, are clearly displayed before you complete your purchase.'
    },
    {
      question: 'How do I sell tickets on BESTIX?',
      answer: 'Selling is easy! Create an account, click "Sell Tickets," and list your tickets with your desired price. Once sold, you\'ll receive payment within 24 hours of the event. We handle buyer communication and secure payment processing.'
    },
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept all major credit cards (Visa, Mastercard, American Express, Discover), PayPal, Apple Pay, and Google Pay. All payments are processed securely through encrypted connections.'
    },
    {
      question: 'What if the event is cancelled or postponed?',
      answer: 'If an event is cancelled, you\'ll receive a full refund automatically. For postponed events, your tickets remain valid for the new date. If you can\'t attend the rescheduled event, contact us for assistance.'
    },
    {
      question: 'How do I contact customer support?',
      answer: 'You can reach us via email at support@bestix.ai, by phone at 1-888-BESTIX-1 (Mon-Fri, 9am-6pm EST), or through our 24/7 live chat. We\'re here to help!'
    }
  ];

  return (
    <>
      <Helmet><title>Help Center | BESTIX</title></Helmet>
      <div className="min-h-screen bg-gray-50">
        <div className="bg-navy py-16">
          <div className="container mx-auto px-4 text-center">
            <h1 className="font-playfair text-4xl font-bold !text-white">Help Center</h1>
            <p className="mt-4 text-xl text-gray-300">Find answers to your questions</p>
          </div>
        </div>

        <div className="container mx-auto px-4 py-16">
          {/* Categories */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {categories.map((category, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow cursor-pointer">
                <div className="w-12 h-12 bg-gold/10 rounded-full flex items-center justify-center mb-4 text-gold">
                  {category.icon}
                </div>
                <h3 className="font-semibold text-navy text-lg mb-2">{category.title}</h3>
                <p className="text-sm text-gray-600">{category.description}</p>
              </div>
            ))}
          </div>

          {/* FAQs */}
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-semibold text-navy mb-8 text-center">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-xl shadow-md overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-medium text-navy">{faq.question}</span>
                    <svg
                      className={`w-5 h-5 text-gray-500 transition-transform ${openFaq === index ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-4">
                      <p className="text-gray-600">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Still Need Help */}
          <div className="mt-16 text-center">
            <div className="bg-navy rounded-xl p-8 max-w-2xl mx-auto">
              <h3 className="text-xl font-semibold text-white !text-white mb-4">Still need help?</h3>
              <p className="text-gray-300 mb-6">Our support team is available 24/7 to assist you.</p>
              <Link to="/contact" className="btn btn-primary">
                Contact Support
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
